import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { settingsApi, GlobalSetting, CreateSettingPayload } from '../../api/settings';
import { cn } from '../../lib/utils';
import { Card } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, Plus } from 'lucide-react';

interface SettingsPageProps {
  isDark: boolean;
}

const settingLabels: Record<string, string> = {
  appFreeForAllUsers: 'App free for all users',
  showHomeBanners: 'Show home banners (mobile carousel)',
};

export default function SettingsPage({ isDark }: SettingsPageProps) {
  const [settings, setSettings] = useState<GlobalSetting[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [updatingKeys, setUpdatingKeys] = useState<Set<string>>(new Set());
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [createForm, setCreateForm] = useState<CreateSettingPayload>({
    key: '',
    value: false,
    description: '',
  });

  const loadSettings = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await settingsApi.list();
      setSettings(data.settings);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch settings');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const settingsByKey = useMemo(() => {
    return new Map(settings.map(setting => [setting.key, setting]));
  }, [settings]);

  const resetCreateForm = () => {
    setCreateForm({ key: '', value: false, description: '' });
  };

  const handleCreate = async () => {
    if (!createForm.key.trim()) {
      toast.error('Key is required');
      return;
    }
    try {
      const payload: CreateSettingPayload = {
        key: createForm.key.trim(),
        value: createForm.value,
        description: createForm.description?.trim() || undefined,
      };
      const created = await settingsApi.create(payload);
      setSettings(prev => {
        const existingIndex = prev.findIndex(setting => setting.key === created.key);
        const next = existingIndex >= 0
          ? prev.map(setting => (setting.key === created.key ? created : setting))
          : [...prev, created];
        return next.sort((a, b) => a.key.localeCompare(b.key));
      });
      toast.success('Setting created');
      resetCreateForm();
      setIsCreateDialogOpen(false);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to create setting');
    }
  };

  const handleToggle = async (key: string, value: boolean) => {
    setUpdatingKeys(prev => new Set(prev).add(key));
    try {
      const updated = await settingsApi.update(key, value);
      setSettings(prev => prev.map(setting => (setting.key === key ? updated : setting)));
      toast.success('Setting updated');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update setting');
      const rollback = settingsByKey.get(key);
      if (rollback) {
        setSettings(prev => prev.map(setting => (setting.key === key ? rollback : setting)));
      }
    } finally {
      setUpdatingKeys(prev => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const renderSettingRow = (setting: GlobalSetting) => {
    const label = settingLabels[setting.key] || setting.key;
    const isUpdating = updatingKeys.has(setting.key);
    return (
      <div
        key={setting.key}
        className={cn(
          'flex items-center justify-between gap-4 p-4 border-b',
          isDark ? 'border-slate-700' : 'border-slate-200'
        )}
      >
        <div className="space-y-1">
          <Label className={cn('text-sm font-medium', isDark ? 'text-white' : 'text-[#0F172A]')}>
            {label}
          </Label>
          {setting.description && (
            <p className={cn('text-xs', isDark ? 'text-slate-400' : 'text-slate-500')}>
              {setting.description}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          {isUpdating && <Loader2 className="w-4 h-4 animate-spin text-[#0EA5A4]" />}
          <Switch
            checked={setting.value}
            onCheckedChange={(checked) => handleToggle(setting.key, checked)}
            disabled={isUpdating}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className={cn('text-2xl lg:text-3xl font-bold tracking-tight', isDark ? 'text-white' : 'text-[#0F172A]')}>
            Settings
          </h1>
          <p className={cn('text-sm mt-1', isDark ? 'text-slate-400' : 'text-slate-500')}>
            Manage global application settings
          </p>
        </div>
        <Button
          onClick={() => setIsCreateDialogOpen(true)}
          className="bg-[#0EA5A4] hover:bg-[#0EA5A4]/90"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Setting
        </Button>
      </div>

      <Card className={cn(
        'rounded-2xl border-0 overflow-hidden transition-all duration-300',
        isDark ? 'bg-[#1E293B] shadow-lg shadow-black/10' : 'bg-white shadow-lg shadow-slate-200/50'
      )}>
        {isLoading ? (
          <div className="flex items-center justify-center p-10">
            <Loader2 className="w-6 h-6 animate-spin text-[#0EA5A4]" />
          </div>
        ) : settings.length === 0 ? (
          <div className="text-center p-10">
            <p className={cn('text-sm', isDark ? 'text-slate-400' : 'text-slate-500')}>
              No settings available
            </p>
          </div>
        ) : (
          <div className="divide-y">
            {settings.map(renderSettingRow)}
          </div>
        )}
      </Card>

      <Dialog
        open={isCreateDialogOpen}
        onOpenChange={(open) => {
          setIsCreateDialogOpen(open);
          if (!open) {
            resetCreateForm();
          }
        }}
      >
        <DialogContent className={cn(
          'max-w-lg',
          isDark ? 'bg-[#1E293B] border-slate-700' : 'bg-white'
        )}>
          <DialogHeader>
            <DialogTitle className={cn(isDark ? 'text-white' : 'text-[#0F172A]')}>
              Create Setting
            </DialogTitle>
            <DialogDescription className={cn(isDark ? 'text-slate-400' : 'text-slate-500')}>
              Add a new global toggle
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Key *</Label>
              <Input
                value={createForm.key}
                onChange={(e) => setCreateForm(prev => ({ ...prev, key: e.target.value }))}
                placeholder="newSettingKey"
                className={cn(
                  isDark
                    ? 'bg-slate-800 border-slate-700 text-white'
                    : 'bg-white border-slate-200'
                )}
              />
            </div>
            <div className="space-y-2">
              <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Description</Label>
              <Input
                value={createForm.description || ''}
                onChange={(e) => setCreateForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Short description"
                className={cn(
                  isDark
                    ? 'bg-slate-800 border-slate-700 text-white'
                    : 'bg-white border-slate-200'
                )}
              />
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                checked={createForm.value}
                onCheckedChange={(checked) => setCreateForm(prev => ({ ...prev, value: checked }))}
              />
              <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Enabled</Label>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsCreateDialogOpen(false);
                resetCreateForm();
              }}
              className={cn(
                isDark
                  ? 'border-slate-700 text-slate-300 hover:bg-slate-800'
                  : 'border-slate-200'
              )}
            >
              Cancel
            </Button>
            <Button onClick={handleCreate} className="bg-[#0EA5A4] hover:bg-[#0EA5A4]/90">
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
